// Content model for the invitation "Help resources" drawer (Bug 10923920). The main drawer
// shows three option cards; selecting one opens a stacked detail drawer that lists a set of
// question / answer / "what to do" entries. All user-facing strings are wrapped in _TL_ for
// build-time extraction.

export type HelpResourceOption = 'newVsExisting' | 'signInHelp' | 'invitationIssues';

export interface HelpResourceQa {
  question: string;
  description: string;
  // Rendered inline after a bold "What to do:" label by the detail drawer.
  whatToDo: string;
}

export interface HelpResourceContent {
  // Shown on the option card in the main drawer AND as the detail drawer header title.
  title: string;
  // Card description in the main drawer.
  subtitle: string;
  items: HelpResourceQa[];
}

// Fixed left-to-right, top-to-bottom order of the three option cards in the main drawer.
export const HELP_RESOURCE_ORDER: HelpResourceOption[] = [
  'newVsExisting',
  'signInHelp',
  'invitationIssues',
];

export const HELP_RESOURCE_CONTENT: Record<HelpResourceOption, HelpResourceContent> = {
  newVsExisting: {
    title: _TL_('Use existing or create account?'),
    subtitle: _TL_('Get help deciding whether to use an existing account or to create a new one.'),
    items: [
      {
        question: _TL_('Should I use an existing account or create an account?'),
        description: _TL_('Use an existing account if you want to access the Microsoft Advertising account you\'re being invited to with an email and sign-in method you\'ve used for Microsoft Advertising before. Create an account if you\'re new to Microsoft Advertising, or if you want to manage this access separately with a different email and sign-in method.'),
        whatToDo: _TL_('Choose {{strong}}Use an existing account{{/strong}} to sign in with an email and sign-in method you\'ve used for Microsoft Advertising before. Choose {{strong}}Create an account{{/strong}} to set up a different email and sign-in method for the account you\'re being invited to.'),
      },
      {
        question: _TL_('Can I use a different email than the invitation was sent to?'),
        description: _TL_('Yes. You can accept the invitation with a different email than the one that received it. The email and sign-in method you choose will be used for your Microsoft Advertising access.'),
        whatToDo: _TL_('Choose the email and sign-in method you want to use going forward.'),
      },
      {
        question: _TL_('Can I use a different email or sign-in method than I used before?'),
        description: _TL_('Yes. You can use a different email or sign-in method than the one you\'ve used for Microsoft Advertising before. This may keep this access separate from your existing Microsoft Advertising access.'),
        whatToDo: _TL_('Choose {{strong}}Create an account{{/strong}} to set up a different email and sign-in method. If you use the wrong email or sign-in method, ask the admin for the Microsoft Advertising account to remove that access and resend your invitation.'),
      },
      {
        question: _TL_('The page says I already have access'),
        description: _TL_('The email and sign-in method you used already has access to this Microsoft Advertising account.'),
        whatToDo: _TL_('Choose {{strong}}Continue signing in{{/strong}} to keep using this email and sign-in method. Choose {{strong}}Use different email{{/strong}} if you want to access this account with a different email or sign-in method.'),
      },
    ],
  },
  signInHelp: {
    title: _TL_('Sign-in help'),
    subtitle: _TL_('Get help with email, sign-in method, work or school accounts, and sign-in errors.'),
    items: [
      {
        question: _TL_('I get an error while signing in'),
        description: _TL_('Sign-in errors can happen because of saved browser data, the wrong sign-in method, or company security settings.'),
        whatToDo: _TL_('Try these steps: open the invitation in a private (InPrivate) window, clear your browser cache, and make sure you\'re using the email and sign-in method you want for Microsoft Advertising. If you\'re using a work or school account and the error continues, contact your company IT admin.'),
      },
      {
        question: _TL_('The page won\'t load or keeps refreshing'),
        description: _TL_('This is usually caused by a browser, network, extension, cache, or VPN setting.'),
        whatToDo: _TL_('Try opening the invitation in a private (InPrivate) window. If that doesn\'t work, try a different browser or network, turn off browser extensions, clear your cache, or turn off your VPN.'),
      },
      {
        question: _TL_('The page says to use Google for this email'),
        description: _TL_('This means you\'ve used Google sign-in with this email to access Microsoft Advertising before.'),
        whatToDo: _TL_('Go back and choose {{strong}}Continue with Google{{/strong}}, then sign in with that email.'),
      },
      {
        question: _TL_('The page says to use Microsoft for this email'),
        description: _TL_('This means you\'ve used Microsoft sign-in with this email to access Microsoft Advertising before.'),
        whatToDo: _TL_('Go back and choose {{strong}}Continue with Microsoft{{/strong}}, then sign in with that email.'),
      },
      {
        question: _TL_('I\'m asked for a Microsoft work or school account'),
        description: _TL_('Some Microsoft Advertising access requires a Microsoft work or school account managed by your company.'),
        whatToDo: _TL_('Choose {{strong}}Sign in with Microsoft{{/strong}} and sign in with your work or school account. If you don\'t have a Microsoft work or school email, contact your company IT admin.'),
      },
      {
        question: _TL_('How do I sign in with a work or school account?'),
        description: _TL_('You need a Microsoft work or school account set up by your company before you can use this option. This is an account provided by your company, not a personal Microsoft or Google account.'),
        whatToDo: _TL_('If you don\'t have one, contact your company IT admin to get one set up. Then choose {{strong}}Sign in with Microsoft{{/strong}} and enter your work or school email. If Microsoft sign-in prompts you to choose between {{strong}}Personal account{{/strong}} and {{strong}}Work or school account{{/strong}}, choose {{strong}}Work or school account{{/strong}}.'),
      },
      {
        question: _TL_('I don\'t have a Microsoft work or school account'),
        description: _TL_('A Microsoft work or school account must be set up by your company. Other sign-in methods may not work for Microsoft Advertising accounts that require work or school sign-in.'),
        whatToDo: _TL_('Contact your company IT admin to get a Microsoft work or school account, or ask the person who invited you if they can send an invitation you can accept another way.'),
      },
    ],
  },
  invitationIssues: {
    title: _TL_('Invitation link problem'),
    subtitle: _TL_('Fix expired, unavailable, or inactive-account invitations.'),
    items: [
      {
        question: _TL_('The invitation link expired'),
        description: _TL_('Invitation links expire 30 days after the invitation is sent.'),
        whatToDo: _TL_('Ask the person who invited you or the admin for the Microsoft Advertising account to resend your invitation.'),
      },
      {
        question: _TL_('The invitation link no longer works'),
        description: _TL_('The invitation can\'t be used because it may have been canceled or already accepted.'),
        whatToDo: _TL_('Check with the person who invited you or the admin for the Microsoft Advertising account to see if the invitation was canceled. If it was canceled, ask them to send you a new invitation. If you think you already accepted the invitation, sign in to Microsoft Advertising as usual with the same email and sign-in method you chose when you accepted it.'),
      },
      {
        question: _TL_('The ads account is inactive'),
        description: _TL_('This invitation can\'t be used because the Microsoft Advertising account is inactive.'),
        whatToDo: _TL_('Contact the person who invited you or the admin for the Microsoft Advertising account. They can reactivate the account or send a new invitation for an active account.'),
      },
    ],
  },
};
